import { useEffect } from "react";

import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import qs from "qs";

import { useNavigate } from "react-router-dom";

import { useSessionStore } from "@/entities/session";
import { useToast } from "../hooks/useToast";
import { toastLocalizationMap } from "../lib/consts";

interface CustomRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_HOST,
});

export const apiV2 = axios.create({
  baseURL: `${import.meta.env.VITE_API_HOST}/v2`,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: "repeat" });
  },
});

type API_VERSION = "v1" | "v2";

export const useApi = ({
  version = "v1",
  errorToast = true,
}: {
  version?: API_VERSION;
  errorToast?: boolean;
} = {}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const targetApi = version === "v1" ? api : apiV2;

  useEffect(() => {
    const requestInterceptor = targetApi.interceptors.request.use((config) => {
      const accessToken = useSessionStore.getState().accessToken;
      if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    });

    const responseInterceptor = targetApi.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomRequestConfig;

        if (error.response?.status === 401 && originalRequest?._retry) {
          originalRequest._retry = false;
          useSessionStore.getState().logout();
        }

        if (
          error.response?.status === 404 &&
          originalRequest.url?.includes("head-contacts")
        ) {
          return;
        }

        if (
          (error.response?.data as { message?: string })?.message ===
          "Client network socket disconnected before secure TLS connection was established"
        ) {
          return;
        }

        if (
          error.response?.status === 401 &&
          originalRequest.url === "/authorization/refresh"
        ) {
          useSessionStore.getState().logout();
        }

        if (error.response?.status === 401 && !originalRequest?._retry) {
          originalRequest._retry = true;

          const refreshToken = useSessionStore.getState().refreshToken;

          if (refreshToken) {
            try {
              const res = await api.post("/authorization/refresh", {
                refreshToken,
              });

              if (res.data) {
                useSessionStore.getState().setAccessToken(res.data.accessToken);
                useSessionStore
                  .getState()
                  .setRefreshToken(res.data.refreshToken);
              } else if (res.status === 401) {
                useSessionStore.getState().logout();
                navigate("/login");
              }
            } catch (refreshError) {
              console.warn(refreshError);
              useSessionStore.getState().logout();
            }
          } else {
            useSessionStore.getState().logout();
          }
        } else if (errorToast) {
          const errorMessage =
            toastLocalizationMap[
              (error.response?.data as { message: string }).message ||
                error.message
            ] ||
            (error.response?.data as { message: string }).message ||
            error.message;

          toast({
            title: "Ой! Ошибка!",
            description: errorMessage,
            variant: "destructive",
          });
        }

        return Promise.reject(error);
      },
    );

    return () => {
      targetApi.interceptors.request.eject(requestInterceptor);
      targetApi.interceptors.response.eject(responseInterceptor);
    };
  }, [toast, targetApi, errorToast, navigate]);

  return { api: targetApi };
};
